import { useState, useEffect, useMemo, Fragment } from "react";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, Link2, Copy, Trash2, Pencil, CheckCircle2, XCircle, ChevronDown, ChevronRight, Building2, Search, MoreHorizontal, Download, Mail, FileDown } from "lucide-react";

const empty = { company: "", name: "", email: "", price_level: "", active: true };

export default function CustomersPanel() {
  const [customers, setCustomers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [query, setQuery] = useState("");
  const [collapsed, setCollapsed] = useState(new Set());
  const [tierFilter, setTierFilter] = useState("All");
  const [sentFilter, setSentFilter] = useState("All");
  const [portalUrl, setPortalUrl] = useState("");

  const load = () => api.get("/admin/customers").then((r) => setCustomers(r.data));
  useEffect(() => {
    load();
    api.get("/admin/meta").then((r) => { setLevels(r.data.levels); setPortalUrl(r.data.portal_url || ""); });
  }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ company: c.company || "", name: c.name, email: c.email, price_level: c.price_level, active: c.active });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name || !form.email || !form.price_level) { toast.error("Name, email and price level are required"); return; }
    if (emailDup) { toast.error(`That email is already used by ${emailDup.company || emailDup.name}.`); return; }
    setBusy(true);
    try {
      if (editing) await api.put(`/admin/customers/${editing.id}`, form);
      else await api.post("/admin/customers", form);
      toast.success(editing ? "Customer updated" : "Customer created");
      setOpen(false);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? This removes their record and pricing access permanently. To only revoke access, edit them and turn off "Active" instead.`)) return;
    await api.delete(`/admin/customers/${c.id}`);
    toast.success("Customer deleted");
    load();
  };

  const universalLink = portalUrl || window.location.origin;
  const copyUniversal = () => {
    navigator.clipboard.writeText(universalLink);
    toast.success("Universal portal link copied");
  };

  const groups = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map();
    for (const c of customers) {
      const company = (c.company && c.company.trim()) || c.name;
      if (tierFilter !== "All" && c.price_level !== tierFilter) continue;
      if (sentFilter === "not_sent" && c.last_link_sent_at) continue;
      if (sentFilter === "sent" && !c.last_link_sent_at) continue;
      if (sentFilter === "pl_not_sent" && c.last_price_list_sent_at) continue;
      if (sentFilter === "pl_sent" && !c.last_price_list_sent_at) continue;
      if (q && !`${company} ${c.name} ${c.email} ${c.price_level}`.toLowerCase().includes(q)) continue;
      if (!map.has(company)) map.set(company, []);
      map.get(company).push(c);
    }
    return [...map.entries()]
      .map(([company, rows]) => ({
        company,
        rows: rows.sort((a, b) => a.name.localeCompare(b.name)),
      }))
      .sort((a, b) => a.company.localeCompare(b.company));
  }, [customers, query, tierFilter, sentFilter]);

  const toggle = (company) => setCollapsed((prev) => {
    const next = new Set(prev);
    next.has(company) ? next.delete(company) : next.add(company);
    return next;
  });
  const slug = (s) => s.replace(/[^a-z0-9]+/gi, "-").toLowerCase();

  const companyOptions = useMemo(
    () => [...new Set(customers.map((c) => (c.company || "").trim()).filter(Boolean))].sort(),
    [customers]
  );

  const tierOptions = useMemo(
    () => [...new Set(customers.map((c) => c.price_level).filter(Boolean))].sort(),
    [customers]
  );

  const companyNorm = form.company.trim().toLowerCase();
  const companyMatch = companyNorm && customers.find((c) => (c.company || "").trim().toLowerCase() === companyNorm && (!editing || c.id !== editing.id));
  const companyLevel = companyMatch ? companyMatch.price_level : "";
  useEffect(() => {
    if (companyLevel && form.price_level !== companyLevel) setForm((f) => ({ ...f, price_level: companyLevel }));
  }, [companyLevel]); // eslint-disable-line

  const emailNorm = form.email.trim().toLowerCase();
  const emailDup = emailNorm && customers.find((c) => c.email.toLowerCase() === emailNorm && (!editing || c.id !== editing.id));

  const exportCsv = async () => {
    try {
      const r = await api.get("/admin/customers/export", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = "thermaco_customers.csv"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Export failed"); }
  };

  const sendLink = async (c) => {
    if (!window.confirm(`Email portal sign-in instructions for ${c.name} (${c.email})?`)) return;
    try {
      const { data } = await api.post(`/admin/customers/${c.id}/send-link`);
      toast.success(data.test_mode ? `Test email sent to ${data.sent_to}` : `Portal link sent to ${data.sent_to}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const sendPriceList = async (c) => {
    if (!window.confirm(`Email ${c.name} (${c.email}) their current price list as a PDF?`)) return;
    try {
      const { data } = await api.post(`/admin/customers/${c.id}/send-price-list`);
      toast.success(data.test_mode ? `Test price list sent to ${data.sent_to}` : `Price list PDF sent to ${data.sent_to}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const sendCompanyPriceLists = async (rows) => {
    const ids = rows.map((r) => r.id);
    if (!window.confirm(`Email the PDF price list to all ${ids.length} contact${ids.length === 1 ? "" : "s"} in this company?`)) return;
    try {
      const { data } = await api.post("/admin/customers/bulk-send-price-list", { ids });
      toast.success(`${data.sent} price list${data.sent === 1 ? "" : "s"} sent${data.failed ? `, ${data.failed} failed` : ""}${data.test_mode ? " (test mode)" : ""}`);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const unsent = useMemo(() => groups.flatMap((g) => g.rows).filter((c) => c.active && !c.last_price_list_sent_at), [groups]);
  const [sendingUnsent, setSendingUnsent] = useState(false);
  const sendToUnsent = async () => {
    if (unsent.length === 0) { toast.info("Everyone in this view has already received the price list."); return; }
    const scope = query || tierFilter !== "All" || sentFilter !== "All" ? "in the current filtered view" : "";
    if (!window.confirm(`Email the PDF price list to ${unsent.length} active customer${unsent.length === 1 ? "" : "s"} ${scope} who haven't received it yet? This sends real emails.`)) return;
    setSendingUnsent(true);
    try {
      const { data } = await api.post("/admin/customers/bulk-send-price-list", { ids: unsent.map((c) => c.id) });
      toast.success(`${data.sent} price list${data.sent === 1 ? "" : "s"} sent${data.failed ? `, ${data.failed} failed` : ""}${data.test_mode ? " (test mode)" : ""}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setSendingUnsent(false); }
  };

  const sentCount = useMemo(() => customers.filter((c) => c.last_price_list_sent_at).length, [customers]);
  const resetSeason = async () => {
    if (!window.confirm(`Start a new season? This clears the "Price list sent" mark on ${sentCount} customer${sentCount === 1 ? "" : "s"} so everyone shows as not yet sent. Portal-link tracking and activity history are kept.`)) return;
    try {
      const { data } = await api.post("/admin/customers/reset-price-list-sent");
      toast.success(`New season started — cleared ${data.cleared} sent mark${data.cleared === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const sendCompanyLinks = async (rows) => {
    const ids = rows.map((r) => r.id);
    if (!window.confirm(`Email the portal link to all ${ids.length} contact${ids.length === 1 ? "" : "s"} in this company?`)) return;
    try {
      const { data } = await api.post("/admin/customers/bulk-send-link", { ids });
      toast.success(`${data.sent} email${data.sent === 1 ? "" : "s"} sent${data.failed ? `, ${data.failed} failed` : ""}${data.test_mode ? " (test mode)" : ""}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  const copyCompanyEmails = async (rows) => {
    const emails = [...new Set(rows.map((r) => r.email).filter(Boolean))];
    if (!emails.length) { toast.error("No emails to copy"); return; }
    await navigator.clipboard.writeText(emails.join(", "));
    toast.success(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}`);
  };

  const copyFilteredEmails = async () => {
    const emails = [...new Set(groups.flatMap((g) => g.rows).map((r) => r.email).filter(Boolean))];
    if (!emails.length) { toast.error("No emails match the current filter"); return; }
    await navigator.clipboard.writeText(emails.join(", "));
    toast.success(`Copied ${emails.length} email${emails.length === 1 ? "" : "s"}`);
  };

  const applyCompany = async (rows, action, price_level) => {
    const ids = rows.map((r) => r.id);
    if (action === "deactivate" && !window.confirm(`Disable portal access for all ${ids.length} contact${ids.length === 1 ? "" : "s"}? They keep their records but can't sign in.`)) return;
    try {
      const { data } = await api.post("/admin/customers/bulk", { ids, action, price_level });
      toast.success(`Updated ${data.modified} contact${data.modified === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <header className="mb-6 flex items-start justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-slate-900">Customers</h1>
          <p className="text-slate-500 mt-1">Everyone uses one shared link and signs in with their email to see only their assigned price level.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={copyFilteredEmails} variant="outline" className="rounded-sm" data-testid="copy-filtered-emails-button">
            <Copy className="h-4 w-4 mr-2" /> Copy emails
          </Button>
          <Button onClick={sendToUnsent} disabled={sendingUnsent} variant="outline" className="rounded-sm border-teal-600 text-teal-700 hover:bg-teal-50 hover:text-teal-800" title="Email the PDF price list to every active customer in this view who hasn't received it yet" data-testid="send-unsent-price-list-button">
            <FileDown className="h-4 w-4 mr-2" /> {sendingUnsent ? "Sending…" : `Price list to unsent (${unsent.length})`}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon" className="rounded-sm" title="More" data-testid="customers-more-menu"><MoreHorizontal className="h-4 w-4" /></Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel className="text-xs text-slate-500">Season rollout</DropdownMenuLabel>
              <DropdownMenuItem onSelect={resetSeason} disabled={sentCount === 0} className="text-red-600 focus:text-red-700" data-testid="reset-season-button">
                Start new season — clear {sentCount} "Price list sent" mark{sentCount === 1 ? "" : "s"}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button onClick={exportCsv} variant="outline" className="rounded-sm" data-testid="export-customers-button">
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
          <Button onClick={openNew} className="rounded-sm bg-slate-900 hover:bg-slate-800 transition-colors" data-testid="new-customer-button">
            <Plus className="h-4 w-4 mr-2" /> New customer
          </Button>
        </div>
      </header>

      <div className="mb-6 bg-slate-900 text-white rounded-sm p-4 flex flex-wrap items-center justify-between gap-3" data-testid="universal-link-banner">
        <div className="flex items-center gap-3 min-w-0">
          <Link2 className="h-5 w-5 text-blue-300 shrink-0" />
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-[0.15em] text-slate-400">Universal portal link</div>
            <div className="font-mono text-sm text-white truncate" data-testid="universal-link-url">{universalLink}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="secondary" className="rounded-sm" onClick={copyUniversal} data-testid="copy-universal-link"><Copy className="h-4 w-4 mr-2" /> Copy link</Button>
          <Button size="sm" variant="outline" className="rounded-sm bg-transparent border-slate-600 text-white hover:bg-slate-800 hover:text-white" onClick={() => window.open(universalLink, "_blank")} data-testid="open-universal-link"><Link2 className="h-4 w-4 mr-2" /> Open</Button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search company, contact or email"
            className="w-full pl-9 pr-3 py-2 rounded-sm border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            data-testid="customer-search"
          />
        </div>
        <div className="text-xs text-slate-400">{customers.length} customers · {groups.length} companies</div>
        <div className="flex gap-2 ml-auto">
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setCollapsed(new Set())} data-testid="expand-all-companies">Expand all</Button>
          <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setCollapsed(new Set(groups.map((g) => g.company)))} data-testid="collapse-all-companies">Collapse all</Button>
        </div>
      </div>

      {(tierOptions.length > 1 || customers.length > 0) && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
          {tierOptions.length > 1 && (
            <div className="flex flex-wrap items-center gap-2" data-testid="tier-filter">
              <span className="text-xs uppercase tracking-[0.1em] text-slate-400 mr-1">Tier</span>
              <button onClick={() => setTierFilter("All")} className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${tierFilter === "All" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`} data-testid="tier-filter-all">All tiers</button>
              {tierOptions.map((t) => (
                <button key={t} onClick={() => setTierFilter(t)} className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${tierFilter === t ? "bg-blue-600 text-white border-blue-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`} data-testid={`tier-filter-${slug(t)}`}>{t}</button>
              ))}
            </div>
          )}
          <div className="flex flex-wrap items-center gap-2 sm:ml-4" data-testid="sent-filter">
            <span className="text-xs uppercase tracking-[0.1em] text-slate-400 mr-1">Emailed</span>
            {[["All", "All"], ["not_sent", "Link not emailed"], ["sent", "Link emailed"], ["pl_not_sent", "Price list not sent"], ["pl_sent", "Price list sent"]].map(([val, label]) => (
              <button key={val} onClick={() => setSentFilter(val)} className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${sentFilter === val ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"}`} data-testid={`sent-filter-${val}`}>{label}</button>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Name</th>
              <th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Price level</th>
              <th className="text-left px-4 py-3">Activity</th>
              <th className="text-center px-4 py-3">Status</th>
              <th className="text-right px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody data-testid="customers-table">
            {groups.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-slate-400">{query ? "No customers match your search." : "No customers yet."}</td></tr>
            )}
            {groups.map((g) => {
              const isCollapsed = collapsed.has(g.company);
              const tiers = [...new Set(g.rows.map((r) => r.price_level))];
              return (
                <Fragment key={g.company}>
                  <tr className="bg-slate-100/70 border-t border-slate-200 cursor-pointer hover:bg-slate-100 transition-colors" onClick={() => toggle(g.company)} data-testid={`company-group-${slug(g.company)}`}>
                    <td colSpan={6} className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        {isCollapsed ? <ChevronRight className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                        <Building2 className="h-4 w-4 text-slate-500" />
                        <span className="font-semibold text-slate-900">{g.company}</span>
                        <span className="text-xs text-slate-400">{g.rows.length} contact{g.rows.length === 1 ? "" : "s"}</span>
                        <div className="ml-auto flex items-center gap-2">
                          <div className="flex flex-wrap gap-1">
                            {tiers.map((t) => <Badge key={t} variant="secondary" className="rounded-sm text-[10px]">{t}</Badge>)}
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button size="icon" variant="ghost" className="h-7 w-7 rounded-sm" onClick={(e) => e.stopPropagation()} data-testid={`company-actions-${slug(g.company)}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
                              <DropdownMenuLabel className="text-xs font-normal text-slate-500">Set tier for all {g.rows.length} contact{g.rows.length === 1 ? "" : "s"}</DropdownMenuLabel>
                              {levels.map((l) => (
                                <DropdownMenuItem key={l} onSelect={() => applyCompany(g.rows, "set_level", l)} data-testid={`company-set-level-${slug(g.company)}-${slug(l)}`}>{l}</DropdownMenuItem>
                              ))}
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onSelect={() => copyCompanyEmails(g.rows)} data-testid={`company-copy-emails-${slug(g.company)}`}>Copy all emails</DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => sendCompanyLinks(g.rows)} data-testid={`company-email-${slug(g.company)}`}>Email portal link to all</DropdownMenuItem>
                              <DropdownMenuItem onSelect={() => sendCompanyPriceLists(g.rows)} data-testid={`company-email-pdf-${slug(g.company)}`}>Email price list PDF to all</DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onSelect={() => applyCompany(g.rows, "activate")} data-testid={`company-enable-${slug(g.company)}`}>Enable all</DropdownMenuItem>
                              <DropdownMenuItem className="text-red-600 focus:text-red-700" onSelect={() => applyCompany(g.rows, "deactivate")} data-testid={`company-disable-${slug(g.company)}`}>Disable all</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </td>
                  </tr>
                  {!isCollapsed && g.rows.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100 hover:bg-blue-50/40 transition-colors">
                      <td className="px-4 py-3 pl-10 font-medium text-slate-900">{c.name}</td>
                      <td className="px-4 py-3 text-slate-600">
                        <div>{c.email}</div>
                        {c.last_link_sent_at ? (
                          <div className="text-[11px] text-indigo-600 mt-0.5" data-testid={`last-emailed-${c.id}`} title={c.last_link_sent_by ? `Sent by ${c.last_link_sent_by}` : ""}>
                            Link sent {new Date(c.last_link_sent_at).toLocaleDateString()}{c.last_link_sent_by ? ` by ${c.last_link_sent_by}` : ""}
                          </div>
                        ) : null}
                        {c.last_price_list_sent_at ? (
                          <div className="text-[11px] text-teal-700 mt-0.5" data-testid={`last-price-list-${c.id}`}>
                            Price list sent {new Date(c.last_price_list_sent_at).toLocaleDateString()}{c.last_price_list_sent_by ? ` by ${c.last_price_list_sent_by}` : ""}
                          </div>
                        ) : null}
                      </td>
                      <td className="px-4 py-3"><Badge className="rounded-sm bg-blue-600">{c.price_level}</Badge></td>
                      <td className="px-4 py-3 text-xs" data-testid={`activity-${c.id}`}>
                        {c.last_access
                          ? (<><div className="text-slate-600">{new Date(c.last_access).toLocaleDateString()} {new Date(c.last_access).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</div><div className="text-slate-400">{c.views} view{c.views === 1 ? "" : "s"}</div></>)
                          : <span className="text-slate-400">No activity</span>}
                      </td>
                      <td className="px-4 py-3 text-center">
                        {c.active
                          ? <span className="inline-flex items-center gap-1 text-green-600 text-xs font-medium"><CheckCircle2 className="h-3.5 w-3.5" /> Active</span>
                          : <span className="inline-flex items-center gap-1 text-slate-400 text-xs font-medium"><XCircle className="h-3.5 w-3.5" /> Disabled</span>}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" title="Email portal link" onClick={() => sendLink(c)} data-testid={`send-link-${c.id}`}><Mail className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" title="Email price list PDF" onClick={() => sendPriceList(c)} data-testid={`send-price-list-${c.id}`}><FileDown className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm" title="Edit" onClick={() => openEdit(c)} data-testid={`edit-customer-${c.id}`}><Pencil className="h-4 w-4" /></Button>
                          <Button size="icon" variant="ghost" className="h-8 w-8 rounded-sm text-red-500 hover:text-red-700" title="Delete" onClick={() => remove(c)} data-testid={`delete-customer-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-sm" data-testid="customer-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit customer" : "New customer"}</DialogTitle></DialogHeader>
          <DialogDescription className="sr-only">Set customer name, contact email, price level and active status.</DialogDescription>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Company</Label>
              <Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} placeholder="e.g. Ferguson Enterprises" className="rounded-sm" list="company-suggestions" autoComplete="off" data-testid="customer-company-input" />
              <datalist id="company-suggestions">
                {companyOptions.map((name) => <option key={name} value={name} />)}
              </datalist>
              <p className="text-xs text-slate-400">Used to group contacts. Start typing to reuse an existing company, or leave blank to use the name below.</p>
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Contact name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="rounded-sm" data-testid="customer-name-input" />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Contact email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={`rounded-sm ${emailDup ? "border-red-400 focus-visible:ring-red-400" : ""}`} data-testid="customer-email-input" />
              {emailDup ? <p className="text-xs text-red-600" data-testid="email-duplicate-warning">Already used by {emailDup.company || emailDup.name} ({emailDup.price_level}).</p> : null}
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-slate-500">Price level</Label>
              <Select value={form.price_level} onValueChange={(v) => setForm({ ...form, price_level: v })} disabled={!!companyLevel}>
                <SelectTrigger className="rounded-sm" data-testid="customer-level-select"><SelectValue placeholder="Select level" /></SelectTrigger>
                <SelectContent>
                  {levels.map((l) => <SelectItem key={l} value={l}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              {companyLevel ? <p className="text-xs text-blue-600" data-testid="company-level-note">Set by company — all {companyMatch.company} contacts share {companyLevel}. Change it from the company menu.</p> : null}
            </div>
            <div className="flex items-center justify-between border border-slate-200 rounded-sm px-3 py-2.5">
              <Label className="text-sm">Active (can sign in)</Label>
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="customer-active-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" className="rounded-sm" onClick={() => setOpen(false)}>Cancel</Button>
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800" disabled={busy} onClick={save} data-testid="save-customer-button">
              {busy ? "Saving…" : editing ? "Save changes" : "Create customer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
